import { Observable } from 'rxjs';
import { IAmrFilterValues, IAzureFiltersSearchApi, ICtFilterValues, ISearchFacet } from '@vivli/features/search/infrastructure/interface';
import { AssignedAppTypeEnum } from '@vivli/shared/infrastructure/enum';
import { ISearchIndexOptions } from '@vivli/core/infrastructure/interface';
import { useSearchIndex } from './use-search-index.hook';

/**
 * Loads the values that populate the filter panels (DEV-1133).
 *
 * The facet definitions below used to be Azure facet-expression strings -
 * `organisms,count:1500,sort:value` - sent verbatim as query parameters, so the client composed the
 * whole expression. They are structured now, and the backend renders the expression from parts it
 * has validated. The resulting expressions are identical to what was sent before; the difference is
 * who assembles them.
 */
export const useFilterValuesSearch = (options: ISearchIndexOptions): IAzureFiltersSearchApi => {
    const searchIndex = useSearchIndex(options);

    const amrFacets: ISearchFacet[] = [
        { field: 'antimicrobials', count: 200, sort: 'value' },
        { field: 'groupingsOfResistancePatterns', count: 50, sort: 'value' },
        { field: 'organisms', count: 1500, sort: 'value' },
        { field: 'specimenSources', count: 200, sort: 'value' },
        { field: 'sampleTimes' },
        { field: 'countries', count: 300, sort: 'value' },
        { field: 'regions' },
        { field: 'yearsDataCollected', count: 50 },
        { field: 'sponsorName', count: 500, sort: 'value' }
    ];

    const ctFacets: ISearchFacet[] = [
        { field: 'studyDesign' },
        { field: 'locationsOfStudySites', count: 300, sort: 'value' },
        { field: 'sponsorType' },
        { field: 'contributorType' },
        { field: 'sponsorName', count: 500, sort: 'value' },
        { field: 'studyType' },
        { field: 'actualEnrollment', interval: 100 },
        { field: 'funder', count: 500 },
        { field: 'orgName', count: 500 }
    ];

    const getFilterValues = <T>(
        facets: ISearchFacet[],
        assignedAppType: AssignedAppTypeEnum = AssignedAppTypeEnum.Default
    ): Observable<T> => {
        return searchIndex.getFacets<T>({
            facets,
            //replaces the raw `assignedAppType eq '...'` string the browser used to build.
            assignedAppType
        });
    };

    const getAmrFilterValues = (): Observable<IAmrFilterValues> => {
        return getFilterValues<IAmrFilterValues>(amrFacets, AssignedAppTypeEnum.Amr);
    };

    const getCtFilterValues = (): Observable<ICtFilterValues> => {
        return getFilterValues<ICtFilterValues>(ctFacets);
    };

    return {
        amrFacets,
        ctFacets,
        getFilterValues,
        getAmrFilterValues,
        getCtFilterValues
    };
};
